import { mergeProps } from "@base-ui/react/merge-props";
import { useRender } from "@base-ui/react/use-render";
import styled from "styled-components/macro";

// shadcn's base-nova Badge (pnpm exec shadcn view badge), in styled-components.
// Departure: tints that need color-mix() or /opacity, newer than our browser floor, are left out: destructive sits on --muted, and default, secondary and destructive links don't change on hover.
// --badge-* fall back to shadcn's values; src/probate/theme.css sets them.

function BadgeElement({ variant = "default", render, ...props }) {
  return useRender({
    defaultTagName: "span",
    props: mergeProps(props),
    render,
    state: { slot: "badge", variant },
  });
}

// Every variant sets border-color, which outranks GlobalStyle's a:hover underline on a link
const Badge = styled(BadgeElement)`
  display: inline-flex;
  box-sizing: border-box;
  width: fit-content;
  min-width: var(--badge-min-width, 0);
  height: var(--juno-space-5);
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  gap: var(--juno-space-1);
  overflow: hidden;
  padding: var(--badge-padding-top, var(--juno-space-half)) var(--badge-padding-inline, var(--juno-space-2))
    var(--badge-padding-bottom, var(--juno-space-half));
  border: 1px solid transparent;
  border-radius: var(--radius-4xl);
  font-size: var(--text-xs);
  line-height: var(--text-xs--line-height);
  font-weight: var(--juno-font-weight-regular);
  white-space: nowrap;

  @media (prefers-reduced-motion: no-preference) {
    transition: all 150ms cubic-bezier(0.4, 0, 0.2, 1);
  }

  /* shadcn's pr-1.5 and pl-1.5, which have no --juno-space step */
  &:has([data-icon="inline-end"]) {
    padding-right: calc(var(--juno-space-1) * 1.5);
  }

  &:has([data-icon="inline-start"]) {
    padding-left: calc(var(--juno-space-1) * 1.5);
  }

  > svg {
    width: var(--juno-space-3);
    height: var(--juno-space-3);
    pointer-events: none;
  }

  &[data-variant="default"] {
    border-color: transparent;
    background-color: var(--primary);
    color: var(--primary-foreground);
  }

  &[data-variant="secondary"] {
    border-color: transparent;
    background-color: var(--secondary);
    color: var(--secondary-foreground);
  }

  &[data-variant="destructive"] {
    border-color: transparent;
    background-color: var(--muted);
    color: var(--destructive);
  }

  &[data-variant="outline"] {
    border-color: var(--border);
    color: var(--foreground);
  }

  &[data-variant="outline"][href]:hover,
  &[data-variant="ghost"]:hover {
    background-color: var(--muted);
    color: var(--muted-foreground);
  }

  &[data-variant="ghost"] {
    border-color: transparent;
  }

  &[data-variant="link"] {
    border-color: transparent;
    color: var(--primary);
    /* shadcn's underline-offset-4 */
    text-underline-offset: 4px;
  }

  &[data-variant="link"]:hover {
    text-decoration: underline;
  }

  /* After the variants, so focus and errors replace their border colour */
  &:focus-visible {
    border-color: var(--ring);
    /* shadcn's ring-[3px] */
    box-shadow: 0 0 0 3px var(--ring);
  }

  &[aria-invalid="true"] {
    border-color: var(--destructive);
  }
`;

export { Badge };
